import React, { useState, useEffect } from "react";
import Card from "components/card";
import { MdEdit, MdPerson, MdLock, MdEmail, MdPhone, MdError, MdVisibility, MdVisibilityOff } from "react-icons/md";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import {jwtDecode} from "jwt-decode";

const Loader = () => (
  <div className="flex h-[400px] items-center justify-center">
    <div className="relative">
      <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
      <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
      <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
      <div className="mt-8 text-center">
        <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
        <p className="text-sm text-gray-400">Please wait...</p>
      </div>
    </div>
  </div>
);

const Settings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userData, setUserData] = useState({
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    role: "",
    profileImage: "/usibras_ghana_limited_logo.jpeg"
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false
  });

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        const loginData = localStorage.getItem('usibras_login');
        const userData = localStorage.getItem('usibras_user_data');
        
        if (!loginData && !userData) {
          throw new Error('No token found');
        }

        let decodedToken;
        
        // Try to parse as JSON first (session manager format)
        if (loginData) {
          try {
            const parsedData = JSON.parse(loginData);
            if (parsedData.userData) {
              // This is session manager format, decode the userData
              decodedToken = jwtDecode(parsedData.userData);
            } else {
              // Fallback to direct JWT token
              decodedToken = jwtDecode(loginData);
            }
          } catch (parseError) {
            // If JSON parsing fails, try as direct JWT token
            decodedToken = jwtDecode(loginData);
          }
        } else if (userData) {
          // Fallback to direct user data
          decodedToken = jwtDecode(userData);
        }
        const userId = decodedToken.id;

        const response = await axios.get(`https://usibras-web-backend.onrender.com/api/profile/${userId}`);
        const { user } = response.data;
        
        setUserData({
          username: user.username,
          firstName: user.first_name,
          lastName: user.last_name,
          email: user.email,
          phone: user.phone || '',
          role: user.role,
          profileImage: "/usibras_ghana_limited_logo.jpeg"
        });
      } catch (err) {
        console.error('Error fetching profile:', err);
        setError('Failed to load profile data');
        toast.error('Failed to load profile data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const loginData = localStorage.getItem('usibras_login');
      const userData = localStorage.getItem('usibras_user_data');
      
      let decodedToken;
      
      // Try to parse as JSON first (session manager format)
      if (loginData) {
        try {
          const parsedData = JSON.parse(loginData);
          if (parsedData.userData) {
            // This is session manager format, decode the userData
            decodedToken = jwtDecode(parsedData.userData);
          } else {
            // Fallback to direct JWT token
            decodedToken = jwtDecode(loginData);
          }
        } catch (parseError) {
          // If JSON parsing fails, try as direct JWT token
          decodedToken = jwtDecode(loginData);
        }
      } else if (userData) {
        // Fallback to direct user data
        decodedToken = jwtDecode(userData);
      }
      const userId = decodedToken.id;

      const response = await axios.put('https://usibras-web-backend.onrender.com/api/edit-profile', {
        user_id: userId,
        email: userData.email,
        first_name: userData.firstName,
        last_name: userData.lastName,
        phone: userData.phone
      });

      if (response.data.success) {
        const { user } = response.data;
        setUserData({
          ...userData,
          email: user.email,
          firstName: user.first_name,
          lastName: user.last_name,
          phone: user.phone,
          role: user.role,
          username: user.username
        });

        setIsEditing(false);
        toast.success(response.data.message);
      } else {
        throw new Error(response.data.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        toast.error('New passwords do not match');
        return;
      }

      const loginData = localStorage.getItem('usibras_login');
      const userData = localStorage.getItem('usibras_user_data');
      
      let decodedToken;
      
      // Try to parse as JSON first (session manager format)
      if (loginData) {
        try {
          const parsedData = JSON.parse(loginData);
          if (parsedData.userData) {
            // This is session manager format, decode the userData
            decodedToken = jwtDecode(parsedData.userData);
          } else {
            // Fallback to direct JWT token
            decodedToken = jwtDecode(loginData);
          }
        } catch (parseError) {
          // If JSON parsing fails, try as direct JWT token
          decodedToken = jwtDecode(loginData);
        }
      } else if (userData) {
        // Fallback to direct user data
        decodedToken = jwtDecode(userData);
      }
      const userId = decodedToken.id;

      const response = await axios.post('https://usibras-web-backend.onrender.com/api/change-password', {
        user_id: userId,
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        confirm_password: passwordData.confirmPassword
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setPasswordData({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.response?.data?.message || 'Failed to change password');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) return <Loader />;

  if (error) {
    return (
      <div className="mt-5 flex h-[400px] items-center justify-center">
        <div className="text-center">
          <MdError className="mx-auto h-16 w-16 text-red-500" />
          <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-white">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 rounded-lg bg-brand-500 px-4 py-2 text-white hover:bg-brand-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 h-full">
      <Toaster position="top-right" />
      
      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-xl shadow-xl flex flex-col items-center">
            <div className="w-12 h-12 border-4 border-brand-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-navy-700 font-medium">Processing...</p>
          </div>
        </div>
      )}

      {/* Profile Image Card */}
      <Card extra={"w-full h-full p-6 mb-6"}>
        <div className="relative flex flex-col items-center">
          <div className="h-32 w-32 rounded-full overflow-hidden border-4 border-white shadow-lg">
            <img
              src={userData.profileImage}
              alt="Profile"
              className="h-full w-full object-cover"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/usibras_ghana_limited_logo.jpeg";
              }}
            />
          </div>
        </div>
      </Card>

      {/* Profile Information Card */}
      <Card extra={"w-full h-full p-6 mb-6"}>
        <div className="relative flex items-center justify-between">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Profile Information
          </h4>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center justify-center rounded-full bg-lightPrimary p-2 text-brand-500 hover:cursor-pointer dark:bg-navy-700"
          >
            <MdEdit className="h-6 w-6" />
          </button>
        </div>

        <div className="mt-6">
          <form onSubmit={handleProfileUpdate}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="text-sm text-navy-700 dark:text-white">First Name</label>
                <div className="flex items-center gap-2">
                  <MdPerson className="h-5 w-5 text-gray-600" />
                  <input
                    type="text"
                    value={userData.firstName}
                    onChange={(e) => setUserData({...userData, firstName: e.target.value})}
                    disabled={!isEditing}
                    className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Last Name</label>
                <div className="flex items-center gap-2">
                  <MdPerson className="h-5 w-5 text-gray-600" />
                  <input
                    type="text"
                    value={userData.lastName}
                    onChange={(e) => setUserData({...userData, lastName: e.target.value})}
                    disabled={!isEditing}
                    className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Username</label>
                <div className="flex items-center gap-2">
                  <MdPerson className="h-5 w-5 text-gray-600" />
                  <input
                    type="text"
                    value={userData.username}
                    disabled
                    className="mt-2 w-full rounded-lg border border-gray-200 bg-gray-100 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white cursor-not-allowed"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Email</label>
                <div className="flex items-center gap-2">
                  <MdEmail className="h-5 w-5 text-gray-600" />
                  <input
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData({...userData, email: e.target.value})}
                    disabled={!isEditing}
                    className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Phone</label>
                <div className="flex items-center gap-2">
                  <MdPhone className="h-5 w-5 text-gray-600" />
                  <input
                    type="tel"
                    value={userData.phone}
                    onChange={(e) => setUserData({...userData, phone: e.target.value})}
                    disabled={!isEditing}
                    className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Role</label>
                <input
                  type="text"
                  value={userData.role}
                  disabled
                  className="mt-2 w-full rounded-lg border border-gray-200 bg-gray-100 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white cursor-not-allowed"
                />
              </div>
            </div>

            {isEditing && (
              <div className="mt-6 flex justify-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>
      </Card>

      {/* Change Password Card */}
      <Card extra={"w-full h-full p-6"}>
        <div className="relative">
          <h4 className="text-xl font-bold text-navy-700 dark:text-white">
            Change Password
          </h4>
        </div>

        <div className="mt-6">
          <form onSubmit={handlePasswordChange}>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label className="text-sm text-navy-700 dark:text-white">Current Password</label>
                <div className="flex items-center gap-2">
                  <MdLock className="h-5 w-5 text-gray-600" />
                  <div className="relative flex-1">
                    <input
                      type={showPasswords.currentPassword ? "text" : "password"}
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, currentPassword: !showPasswords.currentPassword})}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600"
                    >
                      {showPasswords.currentPassword ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">New Password</label>
                <div className="flex items-center gap-2">
                  <MdLock className="h-5 w-5 text-gray-600" />
                  <div className="relative flex-1">
                    <input
                      type={showPasswords.newPassword ? "text" : "password"}
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, newPassword: !showPasswords.newPassword})}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600"
                    >
                      {showPasswords.newPassword ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm text-navy-700 dark:text-white">Confirm New Password</label>
                <div className="flex items-center gap-2">
                  <MdLock className="h-5 w-5 text-gray-600" />
                  <div className="relative flex-1">
                    <input
                      type={showPasswords.confirmPassword ? "text" : "password"}
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="mt-2 w-full rounded-lg border border-gray-200 p-2.5 text-sm text-navy-700 outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords({...showPasswords, confirmPassword: !showPasswords.confirmPassword})}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600"
                    >
                      {showPasswords.confirmPassword ? <MdVisibilityOff className="h-5 w-5" /> : <MdVisibility className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="submit"
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white hover:bg-brand-600"
              >
                Change Password
              </button>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default Settings; 